import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShoppingCart,
  Clock,
  CheckCircle2,
  XCircle,
  Truck,
  Search,
  Filter,
  Calendar,
  MessageSquare,
  Eye,
  AlertTriangle,
  Bell,
  Package,
  Download,
  Check,
  Loader2,
  QrCode,
} from 'lucide-react';
import clsx from 'clsx';
import { useAdminOrders } from '../../services/adminService';
import { useExportOrders } from '../../services/bulkService';
import { getImageUrl } from '../../lib/supabase';
import { formatPrice } from '../../lib/utils';
import { OrderDetailPane } from '../../components/OrderDetailPane/OrderDetailPane';

const STATUS_MAP = {
  pending: { label: 'Pending', color: 'bg-amber-50 text-amber-700', dotColor: 'bg-amber-500', icon: Clock },
  confirmed: { label: 'Confirmed', color: 'bg-blue-50 text-blue-700', dotColor: 'bg-blue-500', icon: CheckCircle2 },
  in_progress: { label: 'In Progress', color: 'bg-purple-50 text-purple-700', dotColor: 'bg-purple-500', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-green-50 text-green-700', dotColor: 'bg-green-500', icon: CheckCircle2 },
  completed: { label: 'Completed', color: 'bg-emerald-50 text-emerald-700', dotColor: 'bg-emerald-600', icon: Check },
  cancelled: { label: 'Cancelled', color: 'bg-red-50 text-error', dotColor: 'bg-red-500', icon: XCircle },
};

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

/**
 * Admin orders page with filters, search, status tabs,
 * server-side CSV export, and detail slide-over.
 */
export default function AdminOrdersPage() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const exportOrders = useExportOrders();

  const { data: orders, isLoading } = useAdminOrders({
    status: statusFilter,
    search: search.length >= 2 ? search : undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo ? `${dateTo}T23:59:59` : undefined,
  });

  // Status counts for tabs
  const statusCounts = useMemo(() => {
    if (!orders) return {};
    const counts = { all: orders.length };
    orders.forEach((o) => {
      counts[o.status] = (counts[o.status] || 0) + 1;
    });
    return counts;
  }, [orders]);

  const unnotifiedCount = useMemo(
    () => (orders || []).filter((o) => !o.notified_owner && o.status === 'pending').length,
    [orders]
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-brand-dark">Orders</h1>
          <p className="text-sm text-brand-gray mt-1">
            {(orders || []).length} order{(orders || []).length !== 1 ? 's' : ''}
            {unnotifiedCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-xs font-bold text-brand-red">
                <Bell size={12} />
                {unnotifiedCount} unread
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters((v) => !v)}
            className={clsx(
              'inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl border transition-colors',
              showFilters
                ? 'border-brand-red text-brand-red bg-brand-red/5'
                : 'border-ui-border text-brand-gray hover:bg-brand-light'
            )}
          >
            <Filter size={16} />
            Filters
          </button>
          <button
            onClick={() => exportOrders.mutate({
              status: statusFilter,
              dateFrom: dateFrom || undefined,
              dateTo: dateTo ? `${dateTo}T23:59:59` : undefined,
            })}
            disabled={exportOrders.isPending}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-brand-gray border border-ui-border hover:bg-brand-light rounded-xl transition-colors"
          >
            {exportOrders.isPending ? (
              <><Loader2 size={16} className="animate-spin" /> Exporting…</>
            ) : (
              <><Download size={16} /> Export CSV</>
            )}
          </button>
        </div>
      </div>

      {/* Status tabs */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        {STATUS_TABS.map((tab) => {
          const count = statusCounts[tab.key] || 0;
          const isActive = statusFilter === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-2 text-sm font-bold rounded-xl transition-colors whitespace-nowrap',
                isActive
                  ? 'bg-brand-red text-brand-white'
                  : 'text-brand-gray hover:bg-brand-light'
              )}
            >
              {tab.label}
              {count > 0 && (
                <span className={clsx(
                  'text-[10px] font-extrabold px-1.5 py-0.5 rounded-full min-w-[18px] text-center',
                  isActive ? 'bg-brand-white/20 text-brand-white' : 'bg-brand-light text-brand-gray'
                )}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search + advanced filters */}
      <div className="space-y-3">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or phone..."
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
          />
        </div>

        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="flex flex-wrap items-center gap-3 p-4 bg-brand-light/50 border border-ui-border rounded-xl">
                <div className="flex items-center gap-2">
                  <Calendar size={14} className="text-brand-gray" />
                  <span className="text-xs font-bold text-brand-gray">From</span>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="px-3 py-1.5 text-sm bg-brand-white border border-ui-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-brand-gray">To</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="px-3 py-1.5 text-sm bg-brand-white border border-ui-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                  />
                </div>
                {(dateFrom || dateTo) && (
                  <button
                    onClick={() => { setDateFrom(''); setDateTo(''); }}
                    className="text-xs font-bold text-brand-red hover:underline"
                  >
                    Clear dates
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Orders list */}
      <div className="bg-brand-white border border-ui-border rounded-2xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="w-12 h-12 bg-brand-light rounded-xl" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-brand-light rounded w-1/3" />
                  <div className="h-3 bg-brand-light rounded w-1/4" />
                </div>
              </div>
            ))}
          </div>
        ) : !orders || orders.length === 0 ? (
          <div className="p-12 text-center">
            <ShoppingCart size={40} className="text-ui-muted mx-auto mb-3" />
            <p className="text-sm font-medium text-brand-gray">No orders found</p>
            {(search || dateFrom || dateTo || statusFilter !== 'all') && (
              <button
                onClick={() => {
                  setSearch('');
                  setDateFrom('');
                  setDateTo('');
                  setStatusFilter('all');
                }}
                className="mt-2 text-sm font-bold text-brand-red hover:underline"
              >
                Clear all filters
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-ui-border">
            {orders.map((order, i) => {
              const statusInfo = STATUS_MAP[order.status] || STATUS_MAP.pending;
              const StatusIcon = statusInfo.icon;
              const productImage = getImageUrl(
                order.product_snapshot?.image_path || order.products?.image_path
              );
              const productTitle = order.product_snapshot?.title || order.products?.title || 'Product';
              const orderTotal = order.total_price || (order.products?.price || 0) * (order.quantity || 1);
              const whatsappUrl = order.whatsapp_link || `https://wa.me/${order.customer_phone?.replace(/\D/g, '')}`;

              return (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.02 }}
                  className={clsx(
                    'flex items-center gap-4 p-4 hover:bg-brand-light/50 transition-colors cursor-pointer',
                    !order.notified_owner && order.status === 'pending' && 'bg-brand-red/[0.03]'
                  )}
                  onClick={() => setSelectedOrder(order)}
                >
                  {/* Image */}
                  {productImage ? (
                    <img
                      src={productImage}
                      alt={productTitle}
                      className="w-12 h-12 rounded-xl object-cover flex-shrink-0 bg-brand-light"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-brand-light flex items-center justify-center flex-shrink-0">
                      <Package size={18} className="text-ui-muted" />
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-brand-dark truncate">
                        {order.customer_name || 'Customer'}
                      </h3>
                      {!order.notified_owner && order.status === 'pending' && (
                        <span className="w-2 h-2 rounded-full bg-brand-red flex-shrink-0" />
                      )}
                      {order.is_duplicate && (
                        <AlertTriangle size={12} className="text-warning flex-shrink-0" />
                      )}
                      {order.qr_code_id && (
                        <span 
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-[10px] font-bold flex-shrink-0"
                          title="Order from QR Code"
                        >
                          <QrCode size={10} />
                          QR
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-brand-gray mt-0.5">
                      {productTitle} × {order.quantity || 1}
                      <span className="mx-1">&bull;</span>
                      {order.customer_phone}
                      {order.source === 'qr_code' && order.qr_code_id && (
                        <>
                          <span className="mx-1">&bull;</span>
                          <span className="text-purple-600 font-medium">via QR</span>
                        </>
                      )}
                    </p>
                  </div>

                  {/* Price + status */}
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-extrabold text-brand-dark">
                      {formatPrice(orderTotal)}
                    </p>
                    <span
                      className={clsx(
                        'inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full mt-1',
                        statusInfo.color
                      )}
                    >
                      <StatusIcon size={10} />
                      {statusInfo.label}
                    </span>
                  </div>

                  {/* Quick actions */}
                  <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setSelectedOrder(order)}
                      className="p-2 rounded-lg text-brand-gray hover:bg-blue-50 hover:text-blue-600 transition-colors"
                      aria-label="View details"
                    >
                      <Eye size={16} />
                    </button>
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg text-brand-gray hover:bg-green-50 hover:text-success transition-colors"
                      aria-label="WhatsApp"
                    >
                      <MessageSquare size={16} />
                    </a>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Time indicator */}
      {orders && orders.length > 0 && (
        <p className="text-xs text-ui-muted text-center">
          Showing {orders.length} order{orders.length !== 1 ? 's' : ''} &bull; Last updated{' '}
          {new Date().toLocaleTimeString()}
        </p>
      )}

      {/* Order detail slide-over */}
      <OrderDetailPane
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
      />
    </div>
  );
}
